import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TaskDetails.css';

function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId]);

  const fetchTaskDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/task-details/${taskId}/`);
      const data = await response.json();
      
      if (response.ok) {
        setTaskData(data);
        // Debug: Log the data structure to console
        console.log('=== FULL API RESPONSE ===');
        console.log('Full task data:', data);
        console.log('=== JOB DATA STRUCTURE ===');
        console.log('Job object:', data.job);
        console.log('Job keys:', data.job ? Object.keys(data.job) : 'job is null/undefined');
        if (data.job) {
          console.log('Job title:', data.job.title);
          console.log('Job company:', data.job.company);
          console.log('Job id:', data.job.id);
        }
        console.log('=== TASK DATA ===');
        console.log('Task object:', data.task);
        console.log('=== LEAD DATA ===');
        console.log('Lead object:', data.lead);
      } else {
        setError(data.error || 'Failed to fetch task details');
      }
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError('Failed to fetch task details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if not a valid date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const updateTaskStatus = async (newStatus) => {
    try {
      const response = await fetch('/api/admin/update-task-status/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId: taskId,
          status: newStatus,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Update the local state
        setTaskData(prev => ({
          ...prev,
          task: {
            ...prev.task,
            status: newStatus
          }
        }));
      } else {
        setError(data.error || 'Failed to update task status');
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status');
    }
  };

  if (loading) {
    return (
      <div className="task-details-container">
        <div className="loading">Loading task details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-details-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!taskData) {
    return (
      <div className="task-details-container">
        <div className="error">Task not found</div>
      </div>
    );
  }

  const { task, lead, job, leadExtracted } = taskData;

  // Add safety check for job object
  const safeJob = job || {};

  return (
    <div className="task-details-container">
      <div className="task-details-header">
        <div className="header-left">
          <h1>Task Details</h1>
        </div>
        <div className="header-right">
          <button 
            onClick={() => navigate('/view-tasks')}
            className="back-button"
          >
            Back to Tasks
          </button>
        </div>
      </div>

      <div className="task-details-content">
        <div className="details-row">
          {/* Task Information */}
          <div className="details-card">
            <h2>Task Information</h2>
            <p className="card-subtitle">Details about the task and its status.</p>
            
            <div className="detail-item">
              <label>Task ID</label>
              <span>{task.id}</span>
            </div>
            
            {/* <div className="detail-item">
              <label>Status</label>
              <select
                value={task.status}
                onChange={(e) => updateTaskStatus(e.target.value)}
                className="status-dropdown"
              >
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ALREADY_APPLIED">Already Applied</option>
                <option value="NOT_RELEVANT">Not Relevant</option>
              </select>
            </div> */}
            
            <div className="detail-item">
              <label>Due Date</label>
              <span>{formatDate(task.dueDate)}</span>
            </div>
            
            <div className="detail-item">
              <label>Created At</label>
              <span>{formatDateTime(task.createdAt)}</span>
            </div>
            
            <div className="detail-item">
              <label>Assigned To</label>
              <span>{task.assignedTo || 'Not assigned'}</span>
            </div>
          </div>

          {/* Lead Information */}
          <div className="details-card">
            <h2>Lead Information</h2>
            <p className="card-subtitle">Details about the lead associated with this task.</p>
            
            <div className="detail-item">
              <label>Lead ID</label>
              <span className="link-text">{lead.id}</span>
            </div>
            
            <div className="detail-item">
              <label>Name</label>
              <span>{lead.name}</span>
            </div>
            
            <div className="detail-item">
              <label>Email</label>
              <span>{lead.email}</span>
            </div>
            
            <div className="detail-item">
              <label>Target Role</label>
              <span>{lead.targetRole || 'Not specified'}</span>
            </div>
            
            <div className="detail-item">
              <label>Years Experience</label>
              <span>{lead.yearsExp || 0} years</span>
            </div>
            
            <div className="detail-item">
              <label>Location</label>
              <span>{lead.location}</span>
            </div>
            
            <div className="detail-item">
              <label>Status</label>
              <span>{lead.status}</span>
            </div>
            
            <div className="detail-item">
              <label>Plan</label>
              <span>{lead.plan}</span>
            </div>
          </div>
        </div>

        {/* Lead Extracted Data */}
        {leadExtracted && (
          <div className="details-card full-width">
            <h2>Lead Extracted Data</h2>
            <p className="card-subtitle">Additional information extracted from the lead's resume and profile.</p>
            
            <div className="detail-item">
              <label>Contact Information</label>
              <div className="contact-info">
                {leadExtracted.contactInfo ? (
                  <span>{leadExtracted.contactInfo}</span>
                ) : (
                  <span>Not available</span>
                )}
              </div>
            </div>
            
            <div className="detail-item">
              <label>Skills</label>
              <div className="skills-container">
                {leadExtracted.skills && leadExtracted.skills.length > 0 ? (
                  leadExtracted.skills.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill.trim()}</span>
                  ))
                ) : (
                  <span>No skills data available</span>
                )}
              </div>
            </div>
            
            <div className="detail-item">
              <label>Work Preferences</label>
              <span>{leadExtracted.workPreferences || 'Not specified'}</span>
            </div>
            
            <div className="detail-item">
              <label>Current Employment</label>
              <span>{leadExtracted.currentEmployment || 'Not specified'}</span>
            </div>
          </div>
        )}

        {/* Job Information */}
        <div className="details-card full-width">
          <h2>Job Information</h2>
          <p className="card-subtitle">Details about the job associated with this task.</p>
          
          <div className="detail-item">
            <label>Job ID</label>
            <span className="link-text">{safeJob.id || safeJob.jobId || safeJob.job_id || 'Not available'}</span>
          </div>
          
          <div className="detail-item">
            <label>Title</label>
            <span>{safeJob.title || safeJob.jobTitle || safeJob.job_title || 'Not available'}</span>
          </div>
          
          <div className="detail-item">
            <label>Company</label>
            <span>{safeJob.company || safeJob.companyName || safeJob.company_name || 'Not available'}</span>
          </div>
          
          <div className="detail-item">
            <label>Location</label>
            <span>{safeJob.location || safeJob.jobLocation || safeJob.job_location || 'Not specified'}</span>
          </div>
          
          <div className="detail-item">
            <label>Role</label>
            <span>{safeJob.role || safeJob.jobRole || safeJob.job_role || safeJob.title || 'Not available'}</span>
          </div>
          
          <div className="detail-item">
            <label>Date Posted</label>
            <span>{formatDate(safeJob.datePosted || safeJob.date_posted || safeJob.postedDate || safeJob.posted_date)}</span>
          </div>
          
          <div className="detail-item">
            <label>Upload Date</label>
            <span>{formatDateTime(safeJob.uploadDate || safeJob.upload_date || safeJob.createdAt || safeJob.created_at)}</span>
          </div>
          
          <div className="detail-item">
            <label>Match Score</label>
            <span className="match-score">{safeJob.matchScore || safeJob.match_score || safeJob.score || 'Not available'}</span>
          </div>
          
          <div className="detail-item">
            <label>Matched Keywords</label>
            <div className="keywords-container">
              {(safeJob.matchedKeywords || safeJob.matched_keywords || safeJob.keywords) && 
               (safeJob.matchedKeywords || safeJob.matched_keywords || safeJob.keywords).length > 0 ? (
                (safeJob.matchedKeywords || safeJob.matched_keywords || safeJob.keywords).map((keyword, index) => (
                  <span key={index} className="keyword-tag">{keyword}</span>
                ))
              ) : (
                <span>No matched keywords available</span>
              )}
            </div>
          </div>
          
          <div className="detail-item">
            <label>Job URL</label>
            {(safeJob.jobUrl || safeJob.job_url || safeJob.url) ? (
              <a href={safeJob.jobUrl || safeJob.job_url || safeJob.url} target="_blank" rel="noopener noreferrer" className="job-link">
                View Job Posting →
              </a>
            ) : (
              <span>No URL available</span>
            )}
          </div>
          
          <div className="detail-item">
            <label>Description</label>
            <div className="job-description">
              {safeJob.description || safeJob.jobDescription || safeJob.job_description || 'No description available'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDetails;
